import Scene from "~/scene";
import SceneLayerMarker from "~/scene/layer/marker";
import SceneLayerGeometry from "~/scene/layer/geometry";
import ActivitiesList from "~/activities/list";
import SceneGeometrySmart from "~/scene/geometry/smart";
import { GEOM_RED_COLOR } from "~/places/lookup";
import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import SceneMarkerMapyWindyPlace from "~/scene/marker/mapy-windy-place";
import { store } from "~/state/store";
import { Unsubscribe } from "redux";
import { watch, watchCoordinates } from "~/state/util";
import createSDSIcon from "~/ui/icon";
import { activityTypeToMapIcon } from "~/scene/common/icons";
import { IMapyBoxActivityFilter } from "~/interfaces";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { activityTypeToTexture } from "~/util/wasm";
import { WASM_ZINDEX } from "~/wasm/const";

export default class Lookup {
	_markerLayer: SceneLayerMarker;

	_geomLayer: SceneLayerGeometry;

	_owner: ActivitiesList;

	_highlightGeometryItem: SceneGeometrySmart;

	_items: {
		[key: string]: {
			title: string;
			subtitle: string;
			markers: Array<SceneMarkerMapyWindyPlace>;
			coords: Array<[number, number]>;
			allCoords: Array<Array<[number, number]>>;
		};
	};

	_timeout: ReturnType<typeof setTimeout>;

	_openDetail: string;

	_storeUnsubscribe: Unsubscribe;

	_onGetData: () => IMapyBoxActivityFilter;

	_wasmMarkers: Array<WasmStoreMarker>;

	_wasmMarkersObj: {[key: string]: WasmStoreMarker};

	_markersVisible: boolean;

	constructor(owner, onGetData: () => IMapyBoxActivityFilter) {
		this._markerLayer = Scene.createLayerMarker() as SceneLayerMarker;
		this._geomLayer = Scene.createLayerGeometry();
		this._owner = owner;
		this._items = {};
		this._timeout = null;
		this._openDetail = "";
		this._onGetData = onGetData;
		this._highlightGeometryItem = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, null, {
			color: GEOM_RED_COLOR,
			width: 4,
			outlineWidth: 0,
		});
		this._storeUnsubscribe = null;
		this._wasmMarkers = [];
		this._wasmMarkersObj = {};
		this._markersVisible = false;
	}

	start() {
		const wt = watch(store.getState, "map", watchCoordinates);

		this._storeUnsubscribe = store.subscribe(wt((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				this.lookup();
			}
		}));
		Scene.addLayer(this._markerLayer).enable();
		Scene.addLayer(this._geomLayer, true).enable();

		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.update({
				visible: true,
			});
		}

		this._markersVisible = true;
	}

	stop() {
		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;
		this._markerLayer.disable();
		Scene.removeLayer(this._markerLayer);
		this._geomLayer.disable();
		Scene.removeLayer(this._geomLayer);

		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.update({
				visible: false,
			});
		}

		this._markersVisible = false;
	}

	disableInteraction() {
		this._markerLayer.disableInteraction();
	}

	enableInteraction() {
		this._markerLayer.enableInteraction();
	}

	clear() {
		Scene.removeCard();

		this._markerLayer.removeAll();

		while (this._wasmMarkers.length) {
			const marker = this._wasmMarkers.pop();

			marker.destroy();
		}

		this._wasmMarkersObj = {};
	}

	openDetail(id?: string) {
		this._openDetail = id || "";

		Scene.removeCard();

		for (const itemId in this._items) {
			if (itemId in this._items) {
				this._items[itemId].markers?.forEach(marker => marker.setActive(!this._openDetail));
			}
		}
	}

	highlight(id?: string) {
		let coords = [];
		let allCoords = [];

		if (this._openDetail === "") {
			const allIds = Object.keys(this._wasmMarkersObj);

			for (const wasmActId of allIds) {
				this._wasmMarkersObj[wasmActId].update({
					visual: !id || wasmActId === id
						? "standard"
						: "deactive",
				});
			}

			for (const itemId in this._items) {
				if (itemId in this._items) {
					this._items[itemId].markers?.forEach(marker => marker.setActive(!id || itemId === id));

					if (id && itemId === id) {
						coords = this._items[itemId].coords;
						allCoords = this._items[itemId].allCoords;
						this._items[itemId].markers && this._items[itemId].markers[1] && this._markerLayer.addMarker(this._items[itemId].markers[1], true);
					} else {
						this._items[itemId].markers && this._items[itemId].markers[1]?.appended && this._markerLayer.removeMarker(this._items[itemId].markers[1], true);
					}
				}
			}

			if (allCoords.length > 1) {
				this._highlightGeometryItem.setCoords(coords);
				this._geomLayer.addSmartGeometry(this._highlightGeometryItem);

				this._highlightGeometryItem.rebind();
			} else if (this._highlightGeometryItem?.getCoords()?.length) {
				this._highlightGeometryItem.setCoords([]);
				this._geomLayer.removeSmartGeometry(this._highlightGeometryItem);
			}

			this._markerLayer.redraw();
		}
	}

	lookup() {
		this._timeout && clearTimeout(this._timeout);
		this._timeout = setTimeout(() => {
			const viewPort = Scene.getViewport();
			const beFilter = this._onGetData();
			const { zoom } = store.getState().map;
			const options = {
				zoom,
				...beFilter || {},
			};

			Mapy.ProviderMapyBox.trackLookup([[viewPort.lbx, viewPort.lby], [viewPort.rtx, viewPort.rty]], options).then(data => {
				if (data.status === 200) {
					if (Mapy.getComponent("activities").state?.mid || Mapy.getComponent("activities").likeData?.linkid) {
						this.openDetail(Mapy.getComponent("activities").state?.mid || Mapy.getComponent("activities").likeData?.linkid);
					}
					this._buildItems(data.items);
				} else {
					this.clear();
				}
			});

			this._timeout = null;
		}, 1000);
	}

	_buildItems(items = []) {
		Scene.removeCard();

		if (!CONFIG.wasmEnabled) {
			this.clear();
		}

		const curWasmIds = Object.keys(this._wasmMarkersObj);

		if (items.length) {
			const newItems = {};
			const keys = Object.keys(this._items);
			const markers = [];

			items.forEach(item => {
				let allCoords = [];
				const coords = [];

				item.data?.data?.forEach(geom => {
					const geomCoords = Scene.stringToCoords(geom.route);

					coords.push(geomCoords);
					allCoords = allCoords.concat(geomCoords);
				});

				newItems[item.id] = {
					title: item.usertitle || item.title,
					subtitle: item.subtitle,
					coords,
					allCoords,
				};

				const ind = curWasmIds.indexOf(item.id);

				if (ind !== -1) {
					curWasmIds.splice(ind, 1);
				}

				if (allCoords.length) {
					let marker = [];

					if (keys.includes(item.id)) {
						const oldData = this._items[item.id].markers[0].getCreateData();

						if (oldData?.item?.data?.info?.trackType === item.data?.info?.trackType) {
							marker = this._items[item.id].markers;
						} else {
							marker = this._createMarker(item, allCoords);
						}
					} else {
						marker = this._createMarker(item, allCoords);
					}

					markers.push(marker[0]);
					newItems[item.id].markers = marker;
				}
			});

			this._items = newItems;

			markers.length && this._markerLayer.addMarker(markers);
		}

		// smazeme stare
		for (const wasmId of curWasmIds) {
			this._wasmMarkersObj[wasmId].destroy();
			delete this._wasmMarkersObj[wasmId];
		}
	}

	_createMarker(item, coords: Array<[number, number]>): Array<SceneMarkerMapyWindyPlace> {
		const iconCover = node("span", { className: "mymap-svg-layer" });
		const ico = createSDSIcon({ symbol: activityTypeToMapIcon(item.data.info.trackType) });

		ico.classList.add("activity");
		iconCover.appendChild(ico);

		const m3dData = {
			type: "places",
			svg: "",
			click: () => {
				this._owner.open(item.id);
			},
			enter: () => {
				this._owner.highlight(item.id);
			},
			leave: () => {
				this._owner.highlight();
			},
		};

		const cardClick = () => {
			this._owner.open(item.id);
		};

		const wasmMarker = new WasmStoreMarker({
			type: "small",
			lon: item.mark.lon,
			lat: item.mark.lat,
			visible: this._markersVisible,
			zIndex: WASM_ZINDEX.marker.activityLookup,
			customData: {
				textureName: activityTypeToTexture(item.data.info.trackType),
				customId: item.id,
				component: "activities",
			},
			callback: "click-hover",
		});

		this._wasmMarkers.push(wasmMarker);
		this._wasmMarkersObj[item.id] = wasmMarker;

		const markers: Array<SceneMarkerMapyWindyPlace> = [
			Scene.createMarkerMapyWindyPlace(coords[0], JSON.parse(JSON.stringify(item)), {
				type: Scene.VISUAL.MARKER_WINDY.SMALL,
				title: Mapy.Util.escapeHTML(item.usertitle ? item.usertitle : item.title),
				elm: iconCover,
				m3dData,
				hrefUrl: ``,
				cardClick,
			}, `${item.id}-0`),
			...coords.length > 1
				? [Scene.createMarkerMapyWindyPlace(coords[coords.length-1], JSON.parse(JSON.stringify(item)), {
					type: Scene.VISUAL.MARKER_WINDY.LITTLE,
					title: Mapy.Util.escapeHTML(item.usertitle ? item.usertitle : item.title),
					m3dData,
					hrefUrl: `${CONFIG.mapyURL}/?moje-mapy&cat=aktivity&mid=${item.id}`,
					cardClick,
				}, `${item.id}-1`)]
				: [],
		];

		if (markers[1]) {
			const markerContainer = markers[1].getContainerSync();

			markerContainer && markerContainer.classList.add("activities-little");
		}

		const markerContainer = markers[0].getContainerSync();

		const cardEnter = (cardEvent: MouseEvent) => {
			if (markerContainer !== cardEvent.relatedTarget && !markerContainer.contains(cardEvent.relatedTarget)) {
				this._owner.highlight(item.id);
			}
		};

		const cardLeave = (cardEvent: MouseEvent) => {
			if (markerContainer !== cardEvent.relatedTarget && !markerContainer.contains(cardEvent.relatedTarget)) {
				this._owner.highlight();

				const cardContainer = markers[0].getCard();

				cardContainer.removeEventListener("mouseenter", cardEnter, false);
				cardContainer.removeEventListener("mouseleave", cardLeave, false);
			}
		};

		markers[0].addEventListener("mouseenter", (event: MouseEvent) => {
			const cardContainer = markers[0].getCard();

			if (cardContainer !== event.relatedTarget && !cardContainer.contains(event.relatedTarget)) {
				this._owner.highlight(item.id);

				cardContainer.addEventListener("mouseenter", cardEnter, false);
				cardContainer.addEventListener("mouseleave", cardLeave, false);
			}
		}, false);
		markers[0].addEventListener("mouseleave", (event: MouseEvent) => {
			const cardContainer = markers[0].getCard();

			if (cardContainer !== event.relatedTarget && !cardContainer.contains(event.relatedTarget)) {
				this._owner.highlight();

				cardContainer.removeEventListener("mouseenter", cardEnter, false);
				cardContainer.removeEventListener("mouseleave", cardLeave, false);
			}
		}, false);
		markers[0].addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
			this._owner.open(item.id);
		}, false);

		return markers;
	}
}
